import { Component } from "react";
// The plain t(), not the hook: this is a class component, and a crash
// screen in English is the last thing a household should meet.
import { t } from "@/i18n";

/**
 * App-wide error boundary. Without this, any uncaught render error unmounts the
 * whole React tree and the user sees a blank screen (just the body watermark).
 * Here we catch it, show a friendly recovery card, and offer a reload. The error
 * text is kept available (collapsed) so issues can still be reported.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || String(error) };
  }

  componentDidCatch(error, info) {
    // Surfaced in the console for debugging; not shown loudly to the user.
    // eslint-disable-next-line no-console
    console.error("App crash caught by ErrorBoundary:", error, info);

    // A "Loading chunk NNN failed" error isn't a real crash: the page was
    // opened on an OLD build and a lazy-loaded file it wants no longer exists
    // after a deploy. Reloading fetches the new build and everything works.
    // Reload ONCE automatically (a sessionStorage flag prevents a reload loop
    // if the chunk is genuinely unreachable, e.g. offline) so the user never
    // sees the error screen for what is only a stale cache.
    try {
      const msg = ((error && error.message) || String(error) || "");
      const isChunkError = /Loading chunk \S+ failed|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(msg);
      if (isChunkError && !sessionStorage.getItem("xel_chunk_reloaded")) {
        sessionStorage.setItem("xel_chunk_reloaded", "1");
        window.location.reload();
      }
    } catch (e) { /* never let the recovery attempt break the boundary */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch (e) { /* noop */ }
  };

  handleBack = () => {
    // Reset the boundary and send the user home - recovers without a full reload
    // when the crash was isolated to one screen.
    this.setState({ hasError: false, message: "" });
    try { window.location.assign("/"); } catch (e) { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="fixed inset-0 z-[999999] flex flex-col items-center justify-center text-center px-8 bg-[var(--bg-primary)] text-gray-900 dark:text-white"
        style={{ background: "var(--bg-primary, #111827)" }}>
        <div className="text-4xl mb-3">🛒</div>
        <h1 className="text-xl font-bold mb-2">{t('errors.title')}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-xs">{t('errors.body')}</p>
        <div className="flex gap-3">
          <button onClick={this.handleReload}
            className="px-5 py-3 rounded-full font-bold bg-black text-white dark:bg-white dark:text-black">{t('errors.reload')}</button>
          <button onClick={this.handleBack}
            className="px-5 py-3 rounded-full font-semibold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100">{t('errors.goHome')}</button>
        </div>
        {this.state.message ? (
          <p className="mt-6 text-[11px] text-gray-400 dark:text-gray-500 max-w-xs break-words opacity-70">
            {this.state.message}
          </p>
        ) : null}
      </div>
    );
  }
}
